import { useMemo, useState } from "react";
import { useFormatter, type ValueFormat } from "@/artifacts/format";
import type { Artifact } from "@/artifacts/types";
import { cn } from "@/lib/cn";

/**
 * A table artifact (PRD FR-6.5): sortable, sticky header, contained scroll,
 * row-count footer.
 *
 * Sorting is client-side and non-destructive — the rows arrive in the order the
 * SQL asked for, and clicking a header is a reader's question rather than a
 * correction to the query. Numbers are formatted from the column's hint but the
 * underlying value is what sorts, so "$1.2M" never sorts as text.
 *
 * On screen it draws rows a page at a time (M8.2). A tile may hold every row
 * the 2 MB result budget admits — 24,000 of them measured, 145,000 cells and
 * 1.7 seconds before anything else on the dashboard could paint — inside a
 * box that shows fifteen. Sorting still covers every row; only the drawing is
 * paged. `paged={false}` is the print route, which is handed a table the
 * server has already cut to its print limit and must draw all of it.
 */
const PAGE = 200;

export function TableArtifact({
  artifact,
  paged = true,
  linked = null,
  onRowClick,
}: {
  artifact: Artifact;
  paged?: boolean;
  /**
   * A linked selection (LINK-2), one flag per data row: `filter` shows only
   * the flagged rows; `mark` keeps them all and marks the flagged ones — the
   * table the selection was made in.
   */
  linked?: { rows: boolean[]; mode: "filter" | "mark" } | null;
  /** A row was clicked, by its position in the data; Cmd/Ctrl adds to the selection. */
  onRowClick?: (row: number, additive: boolean) => void;
}) {
  const formatter = useFormatter();
  const [sort, setSort] = useState<{ column: number; desc: boolean } | null>(null);
  const [shown, setShown] = useState(PAGE);
  const columns = useMemo(() => artifact.data?.columns ?? [], [artifact.data]);
  const rows = useMemo(() => artifact.data?.rows ?? [], [artifact.data]);
  // Each row keeps its position in the data: links address rows by it, and a
  // sort or a filter must not change which row a click means.
  const kept = useMemo(() => {
    const all = rows.map((row, position) => ({ row, position }));
    return linked?.mode === "filter" ? all.filter(({ position }) => linked.rows[position]) : all;
  }, [rows, linked]);

  const ordered = useMemo(() => {
    if (!sort) return kept;
    const { column, desc } = sort;
    const numeric = columns[column]?.numeric ?? false;
    const sorted = [...kept].sort(({ row: left }, { row: right }) => {
      const a = left[column];
      const b = right[column];
      if (a === null || a === undefined) return 1;
      if (b === null || b === undefined) return -1;
      if (numeric) return Number(a) - Number(b);
      return String(a).localeCompare(String(b), formatter.locale, { numeric: true });
    });
    return desc ? sorted.reverse() : sorted;
  }, [kept, sort, columns, formatter.locale]);

  if (!columns.length) {
    return <p className="type-support text-ink-secondary px-4 py-3">This result has no columns.</p>;
  }

  return (
    <div className="flex min-w-0 flex-col">
      <div className="max-h-[26rem] overflow-auto">
        <table className="w-full border-collapse">
          <thead className="bg-surface sticky top-0 z-10">
            <tr>
              {columns.map((column, index) => {
                const active = sort?.column === index;
                return (
                  <th
                    key={column.key + index}
                    scope="col"
                    aria-sort={active ? (sort.desc ? "descending" : "ascending") : "none"}
                    className={cn(
                      "border-border border-b px-3 py-2 whitespace-nowrap",
                      column.numeric ? "text-end" : "text-start",
                    )}
                  >
                    <button
                      type="button"
                      onClick={() =>
                        setSort((current) =>
                          current?.column === index
                            ? current.desc
                              ? null
                              : { column: index, desc: true }
                            : { column: index, desc: false },
                        )
                      }
                      className={cn(
                        "type-label motion-fast hover:text-ink inline-flex items-center gap-1",
                        active ? "text-ink" : "text-ink-secondary",
                      )}
                    >
                      {column.label}
                      <span aria-hidden className="type-caption text-ink-disabled">
                        {active ? (sort.desc ? "▼" : "▲") : ""}
                      </span>
                    </button>
                  </th>
                );
              })}
            </tr>
          </thead>
          <tbody>
            {(paged ? ordered.slice(0, shown) : ordered).map(({ row, position }) => (
              <tr
                key={position}
                aria-selected={linked?.mode === "mark" ? Boolean(linked.rows[position]) : undefined}
                onClick={
                  onRowClick
                    ? (event) => onRowClick(position, event.metaKey || event.ctrlKey)
                    : undefined
                }
                className={cn(
                  "hover:bg-hover motion-fast",
                  onRowClick && "cursor-pointer",
                  linked?.mode === "mark" && linked.rows[position] && "bg-selected",
                )}
              >
                {columns.map((column, index) => (
                  <td
                    key={column.key + index}
                    className={cn(
                      "border-border type-support text-ink border-b px-3 py-1.5",
                      column.numeric ? "text-end tabular-nums" : "text-start",
                    )}
                  >
                    {formatter.value(row[index], column.format as ValueFormat)}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
        {paged && ordered.length > shown ? (
          <div className="flex justify-center px-3 py-2">
            <button
              type="button"
              onClick={() => setShown((current) => current + PAGE * 5)}
              className="type-support text-accent motion-fast hover:underline"
            >
              Show {Math.min(PAGE * 5, ordered.length - shown).toLocaleString(formatter.locale)} more
              rows
            </button>
          </div>
        ) : null}
      </div>
      <footer className="border-border text-ink-disabled type-caption flex flex-wrap items-center gap-2 border-t px-3 py-2">
        {paged && ordered.length > shown ? (
          <span className="tabular-nums">
            {shown.toLocaleString(formatter.locale)} shown ·
          </span>
        ) : null}
        {linked?.mode === "filter" ? (
          <span className="tabular-nums">
            {kept.length.toLocaleString(formatter.locale)} of{" "}
            {rows.length.toLocaleString(formatter.locale)} rows · filtered
          </span>
        ) : (
          <span className="tabular-nums">
            {rows.length.toLocaleString(formatter.locale)} row{rows.length === 1 ? "" : "s"}
          </span>
        )}
        {artifact.data?.total_rows ? (
          <span className="tabular-nums">of {artifact.data.total_rows.toLocaleString(formatter.locale)}</span>
        ) : null}
        {artifact.data?.truncated ? <span>· capped by the source</span> : null}
      </footer>
    </div>
  );
}
